import { useRef, useState } from 'react';
import rawAxios from 'axios';
import toast from 'react-hot-toast';
import AsyncRequestButton from '~/components/AsyncRequestButton';
import {
  BuyerProfileImportSummary,
  getBuyerProfileImportUploadUrl,
  pollBuyerProfileImport,
  startBuyerProfileImport,
} from '~/api/admin';
import { ApiEvent } from '~/types/ApiEvent';
import { ToastDuration } from '~/constants/toasts';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB — mirrors the server limit

interface Props {
  /** Called with the import summary on success (null when a new run starts). */
  onImported?: (summary: BuyerProfileImportSummary | null) => void;
}

export default function UploadBuyerProfiles({ onImported }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Stashed by pollFn on SUCCESS so onSuccess can build the result toast.
  const summaryRef = useRef<BuyerProfileImportSummary | null>(null);
  const [apiEvent, setApiEvent] = useState<ApiEvent | undefined>(undefined);
  const [uploading, setUploading] = useState(false);

  const onFileSelected = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Only .csv files exported from the buyer profile export are supported.');
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error('File is too large — the maximum allowed size is 10MB.');
      return;
    }

    setUploading(true);
    try {
      const { presignedUrl, s3Key } = await getBuyerProfileImportUploadUrl(file.name, file.size);
      // Plain axios: the presigned URL must not receive our auth headers.
      await rawAxios.put(presignedUrl, file);
      const event = await startBuyerProfileImport({ s3Key, fileName: file.name });
      summaryRef.current = null;
      onImported?.(null); // clear any prior run's audit table
      setApiEvent(event);
    } catch (err) {
      console.error(err);
      toast.error('An error occurred uploading the buyer profile workbook. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const showSummaryToasts = () => {
    const summary = summaryRef.current;
    if (!summary) {
      toast.success('Buyer profile import completed.');
      return;
    }
    const changes = summary.answersCreated + summary.answersUpdated + summary.answersDeleted;
    toast.success(
      `Import complete: ${summary.profilesChanged} of ${summary.profilesProcessed} profiles changed (${changes} values added, updated or cleared).`,
      { duration: ToastDuration.LONG }
    );
    if (summary.errors.length > 0) {
      const shown = summary.errors.slice(0, 3)
        .map((e) => `Row ${e.row}: ${e.message}`)
        .join('\n');
      const more = summary.errors.length > 3 ? `\n…and ${summary.errors.length - 3} more rows with errors.` : '';
      toast.error(`Some rows were skipped:\n${shown}${more}`, { duration: ToastDuration.LONG });
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onFileSelected(file);
        }}
      />
      <AsyncRequestButton
        hideEstimatedTime
        apiEvent={apiEvent}
        disabled={uploading}
        onClick={() => {
          fileInputRef.current?.click();
          // Returning undefined aborts the button's own request handling —
          // polling starts via the apiEvent prop once the file is uploaded.
          return undefined;
        }}
        pollFn={async (apiEventId: string) => {
          const result = await pollBuyerProfileImport(apiEventId);
          if (result.status === 'ERROR') {
            throw new Error(result.errorMessage ?? 'Buyer profile import failed');
          }
          if (result.status === 'SUCCESS') {
            summaryRef.current = result.summary ?? null;
            onImported?.(result.summary ?? null);
          }
          return { status: result.status } as ApiEvent;
        }}
        onSuccess={showSummaryToasts}
        onError={() => {
          toast.error('An error occurred importing the buyer profile workbook. No changes from the failed run were applied.');
        }}
        onTimeout={() => {
          toast(
            'The import is taking longer than expected. It will keep running in the background — re-export later to confirm the changes landed.',
            { duration: ToastDuration.LONG }
          );
        }}
        text={uploading ? 'Uploading…' : 'Import Buyer Profiles (.csv)'}
        loadingText="Importing buyer profiles"
      />
    </>
  );
}
